import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import api from "../services/api";
import { fetchStockCatalogs } from "../services/stockCatalogs";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";

const createCategoryForm = () => ({
  name: "",
  description: "",
  is_active: true,
});

const createSupplierForm = () => ({
  name: "",
  contact_name: "",
  email: "",
  phone: "",
  notes: "",
  is_active: true,
});

const createSubcategoryForm = () => ({
  category_id: "",
  name: "",
  description: "",
  is_active: true,
});

const createLocationForm = () => ({
  name: "",
  code: "",
  details: "",
  is_active: true,
});

const StockCatalogs = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryForm, setCategoryForm] = useState(createCategoryForm());
  const [subcategoryForm, setSubcategoryForm] = useState(createSubcategoryForm());
  const [supplierForm, setSupplierForm] = useState(createSupplierForm());
  const [locationForm, setLocationForm] = useState(createLocationForm());
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingSubcategoryId, setEditingSubcategoryId] = useState(null);
  const [editingSupplierId, setEditingSupplierId] = useState(null);
  const [editingLocationId, setEditingLocationId] = useState(null);

  const loadCatalogs = async () => {
    try {
      setLoading(true);
      const data = await fetchStockCatalogs(true);
      setCategories(data.categories);
      setSubcategories(data.subcategories);
      setSuppliers(data.suppliers);
      setLocations(data.locations);
    } catch (error) {
      console.error("Erro ao carregar cadastros de estoque:", error);
      alert("Erro ao carregar cadastros de estoque.");
    } finally {
      setLoading(false);
    }
  };

  const saveSubcategory = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...subcategoryForm,
        category_id: Number(subcategoryForm.category_id),
      };
      if (editingSubcategoryId) {
        await api.put(`/stock/subcategories/${editingSubcategoryId}`, payload);
      } else {
        await api.post("/stock/subcategories", payload);
      }
      setSubcategoryForm(createSubcategoryForm());
      setEditingSubcategoryId(null);
      await loadCatalogs();
    } catch (error) {
      console.error("Erro ao salvar subcategoria:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao salvar subcategoria.");
    }
  };

  useEffect(() => {
    loadCatalogs();
  }, []);

  const saveCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategoryId) {
        await api.put(`/stock/categories/${editingCategoryId}`, categoryForm);
      } else {
        await api.post("/stock/categories", categoryForm);
      }
      setCategoryForm(createCategoryForm());
      setEditingCategoryId(null);
      await loadCatalogs();
    } catch (error) {
      console.error("Erro ao salvar categoria:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao salvar categoria.");
    }
  };

  const saveSupplier = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplierId) {
        await api.put(`/stock/suppliers/${editingSupplierId}`, supplierForm);
      } else {
        await api.post("/stock/suppliers", supplierForm);
      }
      setSupplierForm(createSupplierForm());
      setEditingSupplierId(null);
      await loadCatalogs();
    } catch (error) {
      console.error("Erro ao salvar fornecedor:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao salvar fornecedor.");
    }
  };

  const saveLocation = async (e) => {
    e.preventDefault();
    try {
      if (editingLocationId) {
        await api.put(`/stock/locations/${editingLocationId}`, locationForm);
      } else {
        await api.post("/stock/locations", locationForm);
      }
      setLocationForm(createLocationForm());
      setEditingLocationId(null);
      await loadCatalogs();
    } catch (error) {
      console.error("Erro ao salvar localização:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao salvar localização.");
    }
  };

  const deleteEntity = async (path, onReset) => {
    if (!window.confirm("Tem certeza que deseja excluir este cadastro? Esta ação não pode ser desfeita.")) return;
    try {
      await api.delete(path);
      onReset();
      await loadCatalogs();
    } catch (error) {
      console.error("Erro ao excluir cadastro:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao excluir cadastro.");
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" aria-label="Voltar" onClick={() => navigate("/stock")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Cadastros do Estoque</h1>
            <p className="text-gray-500">
              Categorias, subcategorias, fornecedores e localizações vinculados ao tenant atual.
            </p>
          </div>
        </div>

        {loading ? (
          <Card>
            <CardContent className="pt-6 text-gray-500">Carregando cadastros...</CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-4">
            <Card>
              <CardHeader>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Categorias</h2>
                  <p className="text-sm text-gray-500">Classificação dos materiais.</p>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={saveCategory} className="space-y-3">
                  <Input
                    placeholder="Nome da categoria"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />
                  <textarea
                    placeholder="Descrição"
                    value={categoryForm.description}
                    onChange={(e) =>
                      setCategoryForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                    className="min-h-[84px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  <select
                    value={categoryForm.is_active ? "true" : "false"}
                    onChange={(e) =>
                      setCategoryForm((prev) => ({
                        ...prev,
                        is_active: e.target.value === "true",
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="true">Ativa</option>
                    <option value="false">Inativa</option>
                  </select>
                  <div className="flex gap-2">
                    <Button type="submit">{editingCategoryId ? "Salvar" : "Cadastrar"}</Button>
                    {editingCategoryId && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingCategoryId(null);
                          setCategoryForm(createCategoryForm());
                        }}
                      >
                        Cancelar
                      </Button>
                    )}
                  </div>
                </form>

                <div className="space-y-2">
                  {categories.map((category) => (
                    <div key={category.id} className="rounded-md border border-gray-200 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium text-gray-900">{category.name}</div>
                          <div className="text-xs text-gray-500">
                            {category.is_active ? "Ativa" : "Inativa"}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => {
                              setEditingCategoryId(category.id);
                              setCategoryForm({
                                name: category.name || "",
                                description: category.description || "",
                                is_active: category.is_active !== false,
                              });
                            }}
                            variant="link" className="h-auto p-0 text-xs text-blue-600"
                          >
                            Editar
                          </Button>
                          <Button
                            onClick={() =>
                              deleteEntity(`/stock/categories/${category.id}`, () => {
                                setEditingCategoryId(null);
                                setCategoryForm(createCategoryForm());
                              })
                            }
                            variant="link" className="h-auto p-0 text-xs text-red-600"
                          >
                            Excluir
                          </Button>
                        </div>
                      </div>
                      {category.description && (
                        <p className="mt-2 text-sm text-gray-600">{category.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Subcategorias</h2>
                  <p className="text-sm text-gray-500">Detalhamento da categoria dos materiais.</p>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={saveSubcategory} className="space-y-3">
                  <select
                    value={subcategoryForm.category_id}
                    onChange={(e) =>
                      setSubcategoryForm((prev) => ({ ...prev, category_id: e.target.value }))
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Selecione a categoria</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <Input
                    placeholder="Nome da subcategoria"
                    value={subcategoryForm.name}
                    onChange={(e) => setSubcategoryForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />
                  <textarea
                    placeholder="Descrição"
                    value={subcategoryForm.description}
                    onChange={(e) =>
                      setSubcategoryForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                    className="min-h-[84px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  <select
                    value={subcategoryForm.is_active ? "true" : "false"}
                    onChange={(e) =>
                      setSubcategoryForm((prev) => ({
                        ...prev,
                        is_active: e.target.value === "true",
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="true">Ativa</option>
                    <option value="false">Inativa</option>
                  </select>
                  <div className="flex gap-2">
                    <Button type="submit">{editingSubcategoryId ? "Salvar" : "Cadastrar"}</Button>
                    {editingSubcategoryId && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingSubcategoryId(null);
                          setSubcategoryForm(createSubcategoryForm());
                        }}
                      >
                        Cancelar
                      </Button>
                    )}
                  </div>
                </form>

                <div className="space-y-2">
                  {subcategories.map((subcategory) => (
                    <div key={subcategory.id} className="rounded-md border border-gray-200 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium text-gray-900">{subcategory.name}</div>
                          <div className="text-xs text-gray-500">
                            {subcategory.category_name || "Sem categoria"} |{" "}
                            {subcategory.is_active ? "Ativa" : "Inativa"}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => {
                              setEditingSubcategoryId(subcategory.id);
                              setSubcategoryForm({
                                category_id: subcategory.category_id ? String(subcategory.category_id) : "",
                                name: subcategory.name || "",
                                description: subcategory.description || "",
                                is_active: subcategory.is_active !== false,
                              });
                            }}
                            variant="link" className="h-auto p-0 text-xs text-blue-600"
                          >
                            Editar
                          </Button>
                          <Button
                            onClick={() =>
                              deleteEntity(`/stock/subcategories/${subcategory.id}`, () => {
                                setEditingSubcategoryId(null);
                                setSubcategoryForm(createSubcategoryForm());
                              })
                            }
                            variant="link" className="h-auto p-0 text-xs text-red-600"
                          >
                            Excluir
                          </Button>
                        </div>
                      </div>
                      {subcategory.description && (
                        <p className="mt-2 text-sm text-gray-600">{subcategory.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Fornecedores</h2>
                  <p className="text-sm text-gray-500">Origem dos materiais e parceiros.</p>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={saveSupplier} className="space-y-3">
                  <Input
                    placeholder="Nome do fornecedor"
                    value={supplierForm.name}
                    onChange={(e) => setSupplierForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />
                  <Input
                    placeholder="Contato"
                    value={supplierForm.contact_name}
                    onChange={(e) =>
                      setSupplierForm((prev) => ({ ...prev, contact_name: e.target.value }))
                    }
                  />
                  <Input
                    placeholder="E-mail"
                    value={supplierForm.email}
                    onChange={(e) => setSupplierForm((prev) => ({ ...prev, email: e.target.value }))}
                  />
                  <Input
                    placeholder="Telefone"
                    value={supplierForm.phone}
                    onChange={(e) => setSupplierForm((prev) => ({ ...prev, phone: e.target.value }))}
                  />
                  <textarea
                    placeholder="Observações"
                    value={supplierForm.notes}
                    onChange={(e) => setSupplierForm((prev) => ({ ...prev, notes: e.target.value }))}
                    className="min-h-[84px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  <select
                    value={supplierForm.is_active ? "true" : "false"}
                    onChange={(e) =>
                      setSupplierForm((prev) => ({
                        ...prev,
                        is_active: e.target.value === "true",
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </select>
                  <div className="flex gap-2">
                    <Button type="submit">{editingSupplierId ? "Salvar" : "Cadastrar"}</Button>
                    {editingSupplierId && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingSupplierId(null);
                          setSupplierForm(createSupplierForm());
                        }}
                      >
                        Cancelar
                      </Button>
                    )}
                  </div>
                </form>

                <div className="space-y-2">
                  {suppliers.map((supplier) => (
                    <div key={supplier.id} className="rounded-md border border-gray-200 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium text-gray-900">{supplier.name}</div>
                          <div className="text-xs text-gray-500">
                            {supplier.contact_name || "Sem contato"} |{" "}
                            {supplier.is_active ? "Ativo" : "Inativo"}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => {
                              setEditingSupplierId(supplier.id);
                              setSupplierForm({
                                name: supplier.name || "",
                                contact_name: supplier.contact_name || "",
                                email: supplier.email || "",
                                phone: supplier.phone || "",
                                notes: supplier.notes || "",
                                is_active: supplier.is_active !== false,
                              });
                            }}
                            variant="link" className="h-auto p-0 text-xs text-blue-600"
                          >
                            Editar
                          </Button>
                          <Button
                            onClick={() =>
                              deleteEntity(`/stock/suppliers/${supplier.id}`, () => {
                                setEditingSupplierId(null);
                                setSupplierForm(createSupplierForm());
                              })
                            }
                            variant="link" className="h-auto p-0 text-xs text-red-600"
                          >
                            Excluir
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 text-sm text-gray-600">
                        {[supplier.email, supplier.phone].filter(Boolean).join(" | ") || "Sem contato cadastrado"}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Localizações</h2>
                  <p className="text-sm text-gray-500">Onde cada material fica armazenado.</p>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={saveLocation} className="space-y-3">
                  <Input
                    placeholder="Nome da localização"
                    value={locationForm.name}
                    onChange={(e) => setLocationForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />
                  <Input
                    placeholder="Código"
                    value={locationForm.code}
                    onChange={(e) => setLocationForm((prev) => ({ ...prev, code: e.target.value }))}
                  />
                  <textarea
                    placeholder="Detalhes"
                    value={locationForm.details}
                    onChange={(e) => setLocationForm((prev) => ({ ...prev, details: e.target.value }))}
                    className="min-h-[84px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  <select
                    value={locationForm.is_active ? "true" : "false"}
                    onChange={(e) =>
                      setLocationForm((prev) => ({
                        ...prev,
                        is_active: e.target.value === "true",
                      }))
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="true">Ativa</option>
                    <option value="false">Inativa</option>
                  </select>
                  <div className="flex gap-2">
                    <Button type="submit">{editingLocationId ? "Salvar" : "Cadastrar"}</Button>
                    {editingLocationId && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingLocationId(null);
                          setLocationForm(createLocationForm());
                        }}
                      >
                        Cancelar
                      </Button>
                    )}
                  </div>
                </form>

                <div className="space-y-2">
                  {locations.map((location) => (
                    <div key={location.id} className="rounded-md border border-gray-200 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium text-gray-900">{location.name}</div>
                          <div className="text-xs text-gray-500">
                            {location.code || "Sem código"} |{" "}
                            {location.is_active ? "Ativa" : "Inativa"}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => {
                              setEditingLocationId(location.id);
                              setLocationForm({
                                name: location.name || "",
                                code: location.code || "",
                                details: location.details || "",
                                is_active: location.is_active !== false,
                              });
                            }}
                            variant="link" className="h-auto p-0 text-xs text-blue-600"
                          >
                            Editar
                          </Button>
                          <Button
                            onClick={() =>
                              deleteEntity(`/stock/locations/${location.id}`, () => {
                                setEditingLocationId(null);
                                setLocationForm(createLocationForm());
                              })
                            }
                            variant="link" className="h-auto p-0 text-xs text-red-600"
                          >
                            Excluir
                          </Button>
                        </div>
                      </div>
                      {location.details && (
                        <p className="mt-2 text-sm text-gray-600">{location.details}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StockCatalogs;
